import { Paper } from '@mui/material';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import { useRouter } from 'next/dist/client/router';
import { FunctionComponent, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  currentBrowseRouteSelector,
  playingStateSelector,
  playerModeSelector,
} from '../../redux/slices/playerSlice';
import { PlayingStateEnum } from '../../enums/PlayerEnums';
import { PlayingMode } from '../../redux/types.generated';

import BrowseColour from '/public/icons/layout/browse-colour.svg';
import BrowseGrayscale from '/public/icons/layout/browse-grayscale.svg';
import FavouritesColour from '/public/icons/layout/favourites-colour.svg';
import FavouritesGrayscale from '/public/icons/layout/favourites-grayscale.svg';
import PraharColour from '/public/icons/layout/prahar-colour.svg';
import PraharGrayscale from '/public/icons/layout/prahar-grayscale.svg';
import Link from 'next/link';

import BrowseSelected from '/public/icons/nav-icons/browse-selected.svg';
import BrowseUnselected from '/public/icons/nav-icons/browse-unselected.svg';
import BrowseSelectedPlaying from '/public/icons/navbar-icons/browse-selected-playing.svg';
import BrowseUnselectedPlaying from '/public/icons/navbar-icons/browse-unselected-playing.svg';

import PraharSelected from '/public/icons/nav-icons/prahar-selected.svg';
import PraharUnselected from '/public/icons/nav-icons/prahar-unselected.svg';
import PraharSelectedPlaying from '/public/icons/navbar-icons/prahar-selected-playing.svg';
import PraharUnselectedPlaying from '/public/icons/navbar-icons/prahar-playing.svg';

import FavSelected from '/public/icons/nav-icons/fav-selected.svg';
import FavUnselected from '/public/icons/nav-icons/fav-unselected.svg';
import FavSelectedPlaying from '/public/icons/navbar-icons/fav-selected-playing.svg';
import FavUnselectedPlaying from '/public/icons/navbar-icons/fav-unselected-playing.svg';
import Image from 'next/image';
import PraharSelectedPlayingGif from '/public/icons/nav-icons/prahar-selected-playing.png';
import PraharUnselectedPlayingGif from '/public/icons/nav-icons/prahar-unselected-playing.png';
import BrowseSelectedPlayingGif from '/public/icons/nav-icons/browse-selected-playing.png';
import BrowseUnselectedPlayingGif from '/public/icons/nav-icons/browse-unselected-playing.png';
import FavSelectedPlayingGif from '/public/icons/nav-icons/fav-selected-playing.png';
import FavUnselectedPlayingGif from '/public/icons/nav-icons/fav-unselected-playing.png';
import PremiumFlowerWhite from '/public/icons/premium-flower-white.png';
import { useUserCurrentQuery } from '../../redux/services/auth/auth.generated';
import Cookies from 'js-cookie';
import { praharColorSelector } from '../../redux/slices/praharSlice';

interface BottomBarProps {
  height: number;
}

const BottomBar: FunctionComponent<BottomBarProps> = ({ height }) => {
  const router = useRouter();
  const currentTab = router.asPath;

  const userToken = Cookies.get('ragya-token');
  const { data } = useUserCurrentQuery({}, { skip: !userToken });

  // Exclude routes for unlock button (same as MenuBar)
  const excludeSubscriptionBarRoute =
    /(\/gift)|(\/for-artists)|(\/go-premium)|(\/pricing)|(\/refunds-and-cancellations)|(\/terms-and-conditions)|(\/privacy-policy)|(\/disclaimer)/;
  const excludeSubscriptionBar = excludeSubscriptionBarRoute.test(currentTab);

  // const browseRoute = useSelector(currentBrowseRouteSelector) ?? '/browse';
  const playingState = useSelector(playingStateSelector);
  const playerMode = useSelector(playerModeSelector);

  const isPraharPage = router.asPath.includes('/prahar');
  const praharColor = useSelector(praharColorSelector) || 'transparent';

  const [value, setValue] = useState(currentTab);

  const renderIcon = (
    tabValue: string,
    mode: PlayingMode,
    icons: {
      selected: React.ReactNode;
      unselected: React.ReactNode;
      selectedPlaying: React.ReactNode | string;
      unselectedPlaying: React.ReactNode | string;
    },
    size: { width: string; height: string }
  ) => {
    const isActive = value === tabValue;
    const isPlaying =
      playingState === PlayingStateEnum.Play && playerMode === mode;

    const icon = isPlaying
      ? isActive
        ? icons.selectedPlaying
        : icons.unselectedPlaying
      : isActive
      ? icons.selected
      : icons.unselected;

    if (typeof icon === 'string') {
      return (
        <Image
          src={icon}
          alt={`${tabValue}-icon`}
          width={size.width}
          height={size.height}
          // style={{ objectFit: 'contain' }}
        />
      );
    }

    return icon;
  };

  return (
    <Paper
      sx={{
        position: 'fixed',
        bottom: 0,
        right: 0,
        left: 0,
        height,
        zIndex: 2000,
        boxShadow: 'none', // remove the Paper shadow
        backgroundColor: isPraharPage ? praharColor : 'rgba(255,255,255,0.01)',
        backdropFilter: 'blur(15px)', // soft blur behind
        backgroundImage: 'none',
      }}
      elevation={3}
    >
      <BottomNavigation
        showLabels
        value={value}
        sx={{
          backgroundImage: 'none', // remove any underlying gradients
          backgroundColor: 'transparent', // let the Paper’s rgba show through
          backdropFilter: 'none',
          height: '100%',
        }}
        onChange={(event, newValue) => {
          // don’t do the normal route‐push for the premium button
          if (newValue === '/go-premium') return;
          setValue(newValue);
          router.push(newValue);
        }}
      >
        <BottomNavigationAction
          label='prahar'
          value='/prahar'
          sx={{ height: '100%' }}
          icon={renderIcon(
            '/prahar',
            PlayingMode.Prahar,
            {
              selected: <PraharSelected />,
              unselected: <PraharUnselected />,
              selectedPlaying: PraharSelectedPlayingGif.src,
              unselectedPlaying: PraharUnselectedPlayingGif.src,
            },
            { width: '37px', height: '36px' } // custom size
          )}
        />
        <BottomNavigationAction
          label='browse'
          value={'/browse'}
          sx={{ height: '100%' }}
          icon={renderIcon(
            '/browse',
            PlayingMode.Playlist,
            {
              selected: <BrowseSelected />,
              unselected: <BrowseUnselected />,
              selectedPlaying: BrowseSelectedPlayingGif.src,
              unselectedPlaying: BrowseUnselectedPlayingGif.src,
            },
            { width: '37px', height: '36px' }
          )}
        />
        <BottomNavigationAction
          label='favourites'
          value='/favourites'
          sx={{ height: '100%' }}
          icon={renderIcon(
            '/favourites',
            PlayingMode.Favourite,
            {
              selected: <FavSelected />,
              unselected: <FavUnselected />,
              selectedPlaying: FavSelectedPlayingGif.src,
              unselectedPlaying: FavUnselectedPlayingGif.src,
            },
            { width: '37px', height: '36px' }
          )}
        />
        {!data?.userCurrent?.activeSubscription?.id && !excludeSubscriptionBar && (
          <BottomNavigationAction
            label='premium'
            value='/go-premium'
            sx={{ height: '100%' }}
            icon={
              <Image
                src={PremiumFlowerWhite}
                alt='PremiumFlower'
                width={36}
                height={36}
                // style={{ objectFit: 'contain' }}
              />
            }
            onClick={() =>
              window.open('/go-premium', '_blank', 'noopener,noreferrer')
            }
          />
        )}
      </BottomNavigation>
    </Paper>
  );
};

export default BottomBar;
